import { queryClient } from "@/constants"
import { CLIENT_API } from "@/services/axios-client"
import { useNotificationStore } from "@/states/zustand/notification"
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query"

export const useGetMeditationRetreats = (page: number, year?: number, limit?: number) => {

    const { data, isLoading, ...rest } = useQuery({
        queryKey: ['meditation-retreats', year, page, limit],
        queryFn: () => CLIENT_API.getMeditationRetreats(page, year, limit),
        placeholderData: keepPreviousData,
        // staleTime: 1000 * 60 * 5, // 5 minutes
    })

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useGetMeditationRetreatDetail = (id: string) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ['meditation-retreat', id],
        queryFn: () => CLIENT_API.getMeditationRetreatDetail(id),
        enabled: !!id, // Only fetch if id is truthy
    })

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useCreateMeditationRetreat = () => {
    const { mutate, isPending } = useMutation({
        mutationKey: ['create-meditation-retreat'],
        mutationFn: (payload: CreateMeditationRetreatRequest) => CLIENT_API.createMeditationRetreat(payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ['meditation-retreats']
            });
            useNotificationStore.getState().setNotification({
                type: 'success',
                message: 'Meditation retreat created successfully!'
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: 'error',
                message: error || 'Failed to create meditation retreat.'
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
    }
}

export const useUpdateMeditationRetreat = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["update-meditation-retreat"],
        mutationFn: (payload: { id: number, data: CreateMeditationRetreatRequest }) => CLIENT_API.updateMeditationRetreat(payload.id, payload.data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["meditation-retreats"]
            });
            useNotificationStore.getState().setNotification({
                message: "Meditation Retreat updated successfully!",
                type: "success"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                message: error,
                type: "error"
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }
}

export const useDeleteMeditationRetreat = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["delete-meditation-retreat"],
        mutationFn: (id: number) => CLIENT_API.deleteMeditationRetreat(id),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ["meditation-retreats"]
            });
            useNotificationStore.getState().setNotification({
                message: data?.message,
                type: 'success'
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                message: error,
                type: "error"
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }
}

export const useGetMeditationRetreatByLevels = (page: number, levelIds: number[], limit?: number) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ['meditation-retreats', page, levelIds, limit],
        queryFn: () => CLIENT_API.getMeditationRetreatByLevel(page, levelIds, limit),
        placeholderData: keepPreviousData,
        enabled: levelIds.includes(0) === false && levelIds.length > 0, // Only fetch if levelIds is not empty and does not include 0
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

// statistics data
export const useGetStatisticsData = () => {

    const { data, isLoading, ...rest } = useQuery({
        queryKey: ['statistics'],
        queryFn: () => CLIENT_API.getStatisticsData(),
    });

    return {
        data,
        isLoading,
        ...rest
    }
}